import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import { ClimateChart, InstantOverlayScores } from '../model/climate/climate-chart';
import { ConsolidatedRiskScoreProfile } from '../model/climate/consolidated-risk-score-profile';
import { Observable } from 'rxjs';
import { Point } from '../model/geometry/point';
import { BioclimaticRegion } from '../model/climate/bioclimatic-region';
import { IndicatorValue } from '../account/consolidated-risk-score/model/activity-config.model';

@Injectable({
    providedIn: 'root'
})
export class ClimateService {
    constructor(private readonly http: HttpClient) {
    }

    fetchMaxTemperatureByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/maxTemperature/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchMeanTemperatureByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/meanTemperature/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchMinTemperatureByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/minTemperature/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchSnowfallByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/snowfall/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchPrecipitationByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/precipitation/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchSurfacewindByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/surfaceWind/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchPopulationDensityByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/populationDensity/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchSeaLevelRiseByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/seaLevelRise/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchSeaLevelRiseWarmingByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/seaLevelRiseWarming/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchSpi6ByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/spi6/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchSpi6ScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/spi6Score/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchSpi6ChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/spi6Change/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchCoolingDegreeDaysByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/coolingDegreeDays/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchCoolingDegreeDaysScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/coolingDegreeDaysScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchCoolingDegreeDaysChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/coolingDegreeDaysChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchTxxByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/txx/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchTxxScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/txxScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchTxxChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/txxChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateTByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateT/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateTScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateTChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchFrostDaysByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateFd/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchFrostDaysScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateFdScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchFrostDaysChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateFdChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchHeatingDegreeDaysByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateHd/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchHeatingDegreeDaysScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateHdScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchHeatingDegreeDaysChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateHdChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchExtremeHotDaysByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTx35/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchExtremeHotDaysScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTx35Score/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchExtremeHotDaysChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTx35Change/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isMaxTemperatureEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/maxTemperature/${datasetID}/isEnabled`);
    }

    isMeanTemperatureEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/meanTemperature/${datasetID}/isEnabled`);
    }

    isMinTemperatureEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/minTemperature/${datasetID}/isEnabled`);
    }

    isSnowFallEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/snowFall/${datasetID}/isEnabled`);
    }

    isPrecipitationEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/precipitation/${datasetID}/isEnabled`);
    }

    isSurfaceWindEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/surfaceWind/${datasetID}/isEnabled`);
    }

    isPopulationDensityEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/populationDensity/${datasetID}/isEnabled`);
    }

    isSeaLevelRiseEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/seaLevelRise/${datasetID}/isEnabled`);
    }

    isSeaLevelRiseWarmingEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/seaLevelRiseWarming/${datasetID}/isEnabled`);
    }

    isSpi6Enabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/spi6/${datasetID}/isEnabled`);
    }

    isSpi6ScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/spi6Score/${datasetID}/isEnabled`);
    }
    
    isSpi6ChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/spi6Change/${datasetID}/isEnabled`);
    }
    
    isCoolingDegreeDaysEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/coolingDegreeDays/${datasetID}/isEnabled`);
    }

    isCoolingDegreeDaysScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/coolingDegreeDaysScore/${datasetID}/isEnabled`);
    }

    isCoolingDegreeDaysChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/coolingDegreeDaysChange/${datasetID}/isEnabled`);
    }

    isTxxEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/txx/${datasetID}/isEnabled`);
    }

    isTxxScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/txxScore/${datasetID}/isEnabled`);
    }

    isTxxChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/txxChange/${datasetID}/isEnabled`);
    }

    isClimateTEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateT/${datasetID}/isEnabled`);
    }

    isClimateTScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTScore/${datasetID}/isEnabled`);
    }

    isClimateTChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTChange/${datasetID}/isEnabled`);
    }

    isFrostDaysEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateFd/${datasetID}/isEnabled`);
    }

    isFrostDaysScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateFdScore/${datasetID}/isEnabled`);
    }

    isFrostDaysChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateFdChange/${datasetID}/isEnabled`);
    }

    isHeatingDegreeDaysEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateHd/${datasetID}/isEnabled`);
    }

    isHeatingDegreeDaysScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateHdScore/${datasetID}/isEnabled`);
    }

    isHeatingDegreeDaysChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateHdChange/${datasetID}/isEnabled`);
    }

    isExtremeHotDaysEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTx35/${datasetID}/isEnabled`);
    }

    isExtremeHotDaysScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTx35Score/${datasetID}/isEnabled`);
    }

    isExtremeHotDaysChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTx35Change/${datasetID}/isEnabled`);
    }

    fetchVeryExtremeHotDaysByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTx40/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchVeryExtremeHotDaysScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTx40Score/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchVeryExtremeHotDaysChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTx40Change/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isVeryExtremeHotDaysEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTx40/${datasetID}/isEnabled`);
    }

    isVeryExtremeHotDaysScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTx40Score/${datasetID}/isEnabled`);
    }

    isVeryExtremeHotDaysChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTx40Change/${datasetID}/isEnabled`);
    }

    fetchClimatePrByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climatePr/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimatePrScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climatePrScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimatePrChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climatePrChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isClimatePrEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climatePr/${datasetID}/isEnabled`);
    }

    isClimatePrScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climatePrScore/${datasetID}/isEnabled`);
    }

    isClimatePrChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climatePrChange/${datasetID}/isEnabled`);
    }

    fetchClimateCddByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateCdd/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateCddScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateCddScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateCddChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateCddChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isClimateCddEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateCdd/${datasetID}/isEnabled`);
    }

    isClimateCddScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateCddScore/${datasetID}/isEnabled`);
    }

    isClimateCddChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateCddChange/${datasetID}/isEnabled`);
    }

    fetchClimateRx1dayByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateRx1day/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateRx1dayScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateRx1dayScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateRx1dayChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateRx1dayChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isClimateRx1dayEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateRx1day/${datasetID}/isEnabled`);
    }

    isClimateRx1dayScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateRx1dayScore/${datasetID}/isEnabled`);
    }

    isClimateRx1dayChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateRx1dayChange/${datasetID}/isEnabled`);
    }

    fetchClimateRx5dayByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateRx5day/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateRx5dayScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateRx5dayScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateRx5dayChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateRx5dayChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isClimateRx5dayEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateRx5day/${datasetID}/isEnabled`);
    }

    isClimateRx5dayScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateRx5dayScore/${datasetID}/isEnabled`);
    }

    isClimateRx5dayChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateRx5dayChange/${datasetID}/isEnabled`);
    }

    fetchClimatePrsnByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climatePrsn/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimatePrsnScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climatePrsnScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimatePrsnChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climatePrsnChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isClimatePrsnEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climatePrsn/${datasetID}/isEnabled`);
    }

    isClimatePrsnScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climatePrsnScore/${datasetID}/isEnabled`);
    }

    isClimatePrsnChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climatePrsnChange/${datasetID}/isEnabled`);
    }

    fetchClimateSstByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateSst/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateSstScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateSstScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateSstChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateSstChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isClimateSstEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateSst/${datasetID}/isEnabled`);
    }

    isClimateSstScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateSstScore/${datasetID}/isEnabled`);
    }

    isClimateSstChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateSstChange/${datasetID}/isEnabled`);
    }

    fetchClimateSfcWindByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateSfcWind/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateSfcWindScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateSfcWindScore/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    fetchClimateSfcWindChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateSfcWindChange/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isClimateSfcWindEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateSfcWind/${datasetID}/isEnabled`);
    }

    isClimateSfcWindScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateSfcWindScore/${datasetID}/isEnabled`);
    }

    isClimateSfcWindChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateSfcWindChange/${datasetID}/isEnabled`);
    }

    fetchClimateTrByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTr/${datasetID}?location=${coordinates.y} ${coordinates.x}`);
    }

    fetchClimateTrScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTrScore/${datasetID}?location=${coordinates.y} ${coordinates.x}`);
    }

    fetchClimateTrChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateTrChange/${datasetID}?location=${coordinates.y} ${coordinates.x}`);
    }

    isClimateTrEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTr/${datasetID}/isEnabled`);
    }

    isClimateTrScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTrScore/${datasetID}/isEnabled`);
    }

    isClimateTrChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateTrChange/${datasetID}/isEnabled`);
    }

    fetchClimateSlrByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateSlr/${datasetID}?location=${coordinates.y} ${coordinates.x}`);
    }

    fetchClimateSlrScoreByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateSlrScore/${datasetID}?location=${coordinates.y} ${coordinates.x}`);
    }

    fetchClimateSlrChangeByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart> {
        return this.http.get<ClimateChart>(`/climate/fetch/climateSlrChange/${datasetID}?location=${coordinates.y} ${coordinates.x}`);
    }

    isClimateSlrEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateSlr/${datasetID}/isEnabled`);
    }

    isClimateSlrScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateSlrScore/${datasetID}/isEnabled`);
    }

    isClimateSlrChangeEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/climateSlrChange/${datasetID}/isEnabled`);
    }

    isBioclimaticZonesEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/bioclimaticZones/${datasetID}/isEnabled`);
    }

    /** One chart per indicator for the bioclimatic zone at the location: its levels L1-L5. */
    fetchBioclimaticZonesByLocation(coordinates: Point, datasetID: string): Observable<ClimateChart[]> {
        return this.http.get<ClimateChart[]>(`/climate/fetch/bioclimaticZones/${datasetID}?location=` + coordinates.y + ' ' + coordinates.x);
    }

    /** The bioclimatic zone the location falls in - continent, region and acronym. */
    fetchBioclimaticRegionByLocation(coordinates: Point, datasetID: string): Observable<BioclimaticRegion> {
        return this.http.get<BioclimaticRegion>(`/climate/fetch/bioclimaticZones/${datasetID}/region?location=` + coordinates.y + ' ' + coordinates.x);
    }

    isConsolidatedRiskScoreEnabled(datasetID: string): Observable<boolean> {
        return this.http.get<boolean>(`/climate/consolidatedRiskScore/${datasetID}/isEnabled`);
    }

    getConsolidatedRiskScoreProfiles(datasetID: string): Observable<ConsolidatedRiskScoreProfile[]> {
        return this.http.get<ConsolidatedRiskScoreProfile[]>(`/climate/consolidatedRiskScore/${datasetID}/profiles`);
    }

    fetchConsolidatedRiskScoreByDatapoint(datasetID: string, datapointID: string): Observable<{ [profileId: string]: ClimateChart }> {
        return this.http.get<{ [profileId: string]: ClimateChart }>(`/climate/fetch/consolidatedRiskScore/${datasetID}?datapointId=${datapointID}`);
    }

    /**
     * Address profile equivalent - an address has no datapoint, so the scores are computed
     * live from the overlays at these coordinates instead of read back off a datapoint.
     */
    fetchConsolidatedRiskScoreByLocation(coordinates: Point, datasetID: string): Observable<{ [profileId: string]: ClimateChart }> {
        return this.http.get<{ [profileId: string]: ClimateChart }>(
            `/climate/fetch/consolidatedRiskScore/${datasetID}/byLocation?location=` + coordinates.y + ' ' + coordinates.x);
    }

    /**
     * Instant calculation (FR-1.50-1.56) for a saved location: computes a CRS score from the
     * given percentages, which need not be saved. Nothing here is persisted.
     *
     * Keyed on the datapoint rather than its coordinates so the score comes from the same
     * linkdata the batch run uses, and so the saved percentages reproduce the stored score.
     */
    computeInstantConsolidatedRiskScoreByDatapoint(datasetID: string, datapointID: string, scenario: string | null,
                                                    timePeriod: string, indicatorValues: IndicatorValue[]): Observable<string> {
        let url = `/climate/fetch/consolidatedRiskScore/${datasetID}/instant?datapointId=${datapointID}&timePeriod=${timePeriod}`;
        if (scenario) {
            url += `&scenario=${scenario}`;
        }
        return this.http.post<string>(url, indicatorValues);
    }

    /**
     * The instant calculation card: one vulnerability profile type and its percentages in, a chart
     * covering every scenario and time period out (FR-1.62). Same ClimateChart contract as the
     * stored graph, so the bar, line and tabular views render identically. Nothing is stored.
     */
    computeInstantConsolidatedRiskScoreChart(datasetID: string, datapointID: string, profileId: number,
                                              indicatorValues: IndicatorValue[]): Observable<ClimateChart> {
        const url = `/climate/fetch/consolidatedRiskScore/${datasetID}/instantChart`
            + `?datapointId=${datapointID}&profileId=${profileId}`;
        return this.http.post<ClimateChart>(url, indicatorValues);
    }

    /**
     * The same instant chart for a searched address, which has no datapoint of its own: scored from
     * the indicator Score charts already drawn for the address, since it has no linkdata.
     */
    computeInstantConsolidatedRiskScoreChartByLocation(datasetID: string, profileId: number,
                                                         indicatorValues: IndicatorValue[],
                                                         scoreCharts: InstantOverlayScores[]): Observable<ClimateChart> {
        const url = `/climate/fetch/consolidatedRiskScore/${datasetID}/instantChartByLocation?profileId=${profileId}`;
        return this.http.post<ClimateChart>(url, { indicatorValues, scoreCharts });
    }

    /**
     * The stored consolidated risk score of the saved location at these coordinates - the
     * address panel's "existing" score, read from the same field the location panel reads.
     * Empty when no saved location sits there.
     */
    fetchStoredConsolidatedRiskScoreByLocation(datasetID: string, coordinates: Point, profileId: number,
                                                scenario: string | null, timePeriod: string): Observable<string> {
        let url = `/climate/fetch/consolidatedRiskScore/${datasetID}/storedByLocation?location=${coordinates.y} ${coordinates.x}`
            + `&profileId=${profileId}&timePeriod=${timePeriod}`;
        if (scenario) {
            url += `&scenario=${scenario}`;
        }
        return this.http.get(url, { responseType: 'text' as 'json' }) as Observable<string>;
    }

    /**
     * Address panel equivalent. The score still comes from the saved location at these
     * coordinates rather than from a live overlay sample, so it always agrees with the
     * stored value - an address with no saved location nearby has no score.
     */
    computeInstantConsolidatedRiskScore(datasetID: string, coordinates: Point, scenario: string | null,
                                         timePeriod: string, indicatorValues: IndicatorValue[]): Observable<string> {
        let url = `/climate/fetch/consolidatedRiskScore/${datasetID}/instant?location=${coordinates.y} ${coordinates.x}&timePeriod=${timePeriod}`;
        if (scenario) {
            url += `&scenario=${scenario}`;
        }
        return this.http.post<string>(url, indicatorValues);
    }
}